/**
 * Message Type Definition
 * @typedef {Object} Message
 * @property {string} id - Unique identifier for the message (auto-generated by Firestore)
 * @property {string} text - Message content
 * @property {string} senderId - User ID of the sender
 * @property {string} senderName - Display name of the sender
 * @property {string} senderEmail - Email of the sender
 * @property {string} receiverId - User ID of the receiver
 * @property {string} chatId - Unique chat room ID (combination of user IDs)
 * @property {firebase.firestore.Timestamp} timestamp - Message creation timestamp
 * @property {string} type - Message type (text, image, file)
 * @property {string} [fileUrl] - URL for file/image messages
 * @property {string} [fileName] - Original filename for file messages
 * @property {boolean} isRead - Whether the message has been read
 */

/**
 * Message type constants
 */
export const MESSAGE_TYPE = {
  TEXT: 'text',
  IMAGE: 'image',
  FILE: 'file'
};

/**
 * Creates a new message object
 * @param {Object} params - Message parameters
 * @param {string} params.text - Message content
 * @param {string} params.senderId - User ID of the sender
 * @param {string} params.senderName - Display name of the sender
 * @param {string} params.senderEmail - Email of the sender
 * @param {string} params.receiverId - User ID of the receiver
 * @param {string} params.chatId - Chat room ID
 * @param {string} params.type - Message type
 * @param {string} params.fileUrl - File URL (optional)
 * @param {string} params.fileName - File name (optional)
 * @returns {Omit<Message, 'id' | 'timestamp' | 'isRead'>} - New message object
 */
export const createMessage = ({
  text,
  senderId,
  senderName,
  senderEmail,
  receiverId,
  chatId,
  type = MESSAGE_TYPE.TEXT,
  fileUrl = '',
  fileName = ''
}) => ({
  text: text.trim(),
  senderId,
  senderName,
  senderEmail,
  receiverId,
  chatId,
  type,
  fileUrl,
  fileName,
  timestamp: new Date(),
  isRead: false
});

/**
 * Validates a message object
 * @param {Object} message - Message object to validate
 * @returns {Object} - Validation result { isValid: boolean, errors: string[] }
 */
export const validateMessage = (message) => {
  const errors = [];

  if (!message.text?.trim() && message.type === MESSAGE_TYPE.TEXT) {
    errors.push('Message text is required');
  }

  if (!message.senderId?.trim()) errors.push('Sender ID is required');
  if (!message.senderName?.trim()) errors.push('Sender name is required');
  if (!message.receiverId?.trim()) errors.push('Receiver ID is required');
  if (!message.chatId?.trim()) errors.push('Chat ID is required');

  if (message.type === MESSAGE_TYPE.FILE && !message.fileUrl?.trim()) {
    errors.push('File URL is required for file messages');
  }

  if (message.text && message.text.length > 1000) {
    errors.push('Message text must be less than 1000 characters');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Generates a unique chat ID from two user IDs
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @returns {string} - Generated chat ID
 */
export const generateChatId = (userId1, userId2) => {
  return [userId1, userId2].sort().join('_');
};

export default {
  MESSAGE_TYPE,
  createMessage,
  validateMessage,
  generateChatId
};